import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import { useEffect } from 'react';
import React, { useCallback, useState } from 'react';

import { ButtonLink, ButtonPrimary } from '@pinvest/sirius-button';
import { Divider } from '@pinvest/sirius-divider';
import { TextField } from '@pinvest/sirius-input';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import MetaHead from '@/components/shared/MetaHead';
import MultiChipSelect from '@/components/shared/MultipleChipSelect';
import NavigationBar from '@/components/shared/NavigationBar';
import OTPForm from '@/components/shared/OTPForm';
import AppTemplate from '@/components/shared/templates/AppTemplate';
import CenterContentTemplate from '@/components/shared/templates/CenterContentTemplate';
import Text from '@/components/shared/Text';
import { waPinfinance2 } from '@/constants/externalUrls';
import { dompetKilatLogoUrl, ojkLogoUrl } from '@/constants/images';
import internalUrls from '@/constants/internalUrls';
import { VerificationType, verificationTypeOptions } from '@/constants/verification';
import { verificationTypeMapByValue } from '@/constants/verification';
import { useFeatureFlag } from '@/contexts/featureFlag';
import { useOAuth } from '@/contexts/oauth';
import useRequestOtp from '@/repositories/auth/requestOtp/useRequestOtp';
import { fetchUserInfo } from '@/repositories/auth/userInfo';
import type { ResponseVerifyOtp } from '@/repositories/auth/verifyOtp/types';
import { useVerifyLoginOtp } from '@/repositories/auth/verifyOtp/useVerifyOtp';
import type { FetchError } from '@/repositories/utils';
import { parseError } from '@/repositories/utils';
import cookie from '@/services/cookies';
import EventTrackingService from '@/services/eventTracking';
import { signInGetResponse, signInOpenPage } from '@/services/eventTracking/events/signIn';
import { signInEvent } from '@/services/eventTracking/events/signIn/constants';
import { signUpEvent } from '@/services/eventTracking/events/signUp/constants';
import { SignUpAttr } from '@/services/eventTracking/events/signUp/types';
import type { BaseResponseApi } from '@/types/general/responseApi';

import { metaTitle } from './config';
import styles from './styles.module.css';
import type { LoginStep } from './types';
import { validators } from './utils';

const LoginPage: React.FC = () => {
  const { enable_modify_registration_flow } = useFeatureFlag();

  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [verificationMethod, setVerificationMethod] = useState<string>(VerificationType.WHATSAPP);
  const [step, setStep] = useState<LoginStep>('login');
  const [secretCode, setSecretCode] = useState<string | null>(null);
  const [errorVerifyOtp, setErrorVerifyOtp] = useState('');
  const router = useRouter();

  const [trackingAttribute, setTrackingAttribute] = useState('');

  const { login } = useOAuth();

  const handleChangeVerificationMethod = useCallback((newValue: string | string[]) => {
    if (typeof newValue == 'string') {
      setVerificationMethod(newValue);
    }
  }, []);

  const handleChangePhoneNumber = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const validatedValue = e.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(validatedValue);
    setPhoneError(validators.phoneNumber(validatedValue));
  }, []);

  const handleRequestOtpSuccess = useCallback(() => {
    setPhoneError(undefined);
    setStep('otp-verification');
  }, []);

  const handleRequestOtpError = useCallback(async (err: FetchError) => {
    const res = await parseError(err);

    if (res.data?.message == 'phone number is not registered') {
      setPhoneError('Nomor handphone belum terdaftar');
      return;
    } else if (res.data?.message.includes('2005505')) {
      setPhoneError('Coba beberapa saat lagi');
      return;
    } else if (res.data?.message == 'customer type not allowed to login') {
      setPhoneError('Nomor handphone ini terdaftar sebagai akun personal, silakan masuk akun melalui aplikasi Rekan Agen Pinhome');
      return;
    }

    setPhoneError('Terjadi kesalahan pada server');
  }, []);

  const {
    data: requestOtpRep,
    isLoading: isRequestingOtp,
    fetch: requestOtp
  } = useRequestOtp({
    onSuccess: handleRequestOtpSuccess,
    onError: handleRequestOtpError
  });

  useEffect(() => {
    if (requestOtpRep?.data?.secretCode != null) {
      setSecretCode(requestOtpRep.data.secretCode);
    }

  }, [requestOtpRep]);

  useEffect(() => {
    const res = cookie.get(signInEvent.openPage);

    cookie.delete(signInEvent.openPage);

    const parsedRes = res ? JSON.parse(res) : null;

    if (parsedRes?.attribute) {
      setTrackingAttribute(parsedRes.attribute);
      EventTrackingService.pushEvent(signInOpenPage(
        {
          attribute: parsedRes.attribute
        }
      ));
    }
  }, []);

  const handleOnBackButton = useCallback(() => {
    if (step === 'otp-verification') {
      setStep('login');
      setErrorVerifyOtp('');
      return;
    }

    router.back();
  }, [router, step]);

  const handleValidate = useCallback((): boolean => {
    const errPhoneNumber = validators.phoneNumber(phoneNumber);
    if (errPhoneNumber) setPhoneError(errPhoneNumber);

    return !!errPhoneNumber;
  }, [phoneNumber]);

  const handleRequestOtp = useCallback(() => {
    if (isRequestingOtp) return;

    const err = handleValidate();
    if (err) return;

    requestOtp({
      medium: verificationMethod,
      otpType: 'login',
      countryCode: '62',
      phoneNumber: phoneNumber
    });
  }, [handleValidate, isRequestingOtp, phoneNumber, requestOtp, verificationMethod]);

  const handleVerifyOtpError = useCallback(async (err: FetchError) => {
    const res = await parseError(err);

    if (res) {
      setErrorVerifyOtp('Pastikan kode yang Anda masukkan sudah benar');
    }
  }, []);

  const handleVerifyOtpSuccess = useCallback(async (resp: BaseResponseApi<ResponseVerifyOtp>) => {
    if (resp?.data.token) {
      const userInfo = await fetchUserInfo(resp.data.token);

      if (trackingAttribute) {
        EventTrackingService.pushEvent(signInGetResponse(
          {
            customer_guid: userInfo.data.guid,
            attribute: trackingAttribute
          }
        ));
      }

      login({
        accessToken: resp.data.token,
        ...userInfo.data
      });
    }

    const redirectPath = router.query?.redirect?.toString() ?? '';

    if (!redirectPath) {
      router.push(internalUrls.dasboard);
      return;
    }

    router.push(redirectPath);

  }, [login, router, trackingAttribute]);

  const {
    fetch: verifyOtp
  } = useVerifyLoginOtp({
    onSuccess: handleVerifyOtpSuccess,
    onError: handleVerifyOtpError
  });

  const handleVerifyOtp = useCallback((verificationCode: string) => {
    if (secretCode != null && verificationCode) {
      verifyOtp({
        medium: verificationMethod,
        otpType: 'login',
        countryCode: '62',
        phoneNumber: phoneNumber,
        secretCode: secretCode,
        verificationCode: verificationCode
      });
    }
  }, [phoneNumber, secretCode, verificationMethod, verifyOtp]);

  const disableButton = useMemo(
    () =>
      isRequestingOtp || phoneError != undefined || phoneNumber == ''
    , [isRequestingOtp, phoneError, phoneNumber]);

  const handleSignUpOnClicked = useCallback(() => {
    cookie.add({
      name: signUpEvent.openPage,
      value: JSON.stringify({ attribute: SignUpAttr.SIGN_IN_FORM })
    });
  }, []);

  return (
    <AppTemplate showFooter={false} color='subtle'>
      <MetaHead title={metaTitle} />
      <div className={styles['login-page__navbar']}>
        <NavigationBar isShowMenu={false} />
      </div>
      <CenterContentTemplate
        showBackButton={step == 'otp-verification'}
        title={`${step == 'login' ? 'Masuk ke Akun Pinhome Financial' : 'Masukan Kode Verifikasi'}`}
        onBack={handleOnBackButton}>
        {step == 'login' &&
          <form className={styles['login-page__form']} data-testid='frm-login' onSubmit={handleRequestOtp}>
            <div className={styles['login-page__title']}>
              <Text component='h2' typography='heading1'>Masuk Akun Pinhome Financial</Text>
            </div>
            <Text
              component='h2'
              typography='body2'
              margin='0 0 16px 0'
            >
              Masukkan nomor handphone Anda untuk masuk.
            </Text>
            <TextField
              prefix='+62'
              placeholder='Ketik nomor handphone Anda'
              type='tel'
              value={phoneNumber}
              onChange={handleChangePhoneNumber}
              description={phoneError}
              hasError={!!phoneError}
              data-testid='txt-phoneNumber'
            />
            <Text component='p' typography='label2' margin='16px 0 10px 0'>Terima Kode Verifikasi Melalui:</Text>
            <div className={styles['login-page__chip']}>
              <MultiChipSelect
                groupName='verification-method'
                type='radio'
                options={verificationTypeOptions}
                values={verificationMethod}
                onChange={handleChangeVerificationMethod}
                testId='chip-verification-method'
              />
            </div>
            <ButtonPrimary
              testId='btn-masuk-login'
              fullWidth
              disabled={disableButton}
              onClick={handleRequestOtp}
            >
              Masuk
            </ButtonPrimary>
            <Text
              typography='body2'
              margin='17px 0 0 0'
              textAlign='center'
              component='p'
            >
              Belum punya akun? {' '}
              <ButtonLink href={internalUrls.register} onClick={handleSignUpOnClicked} testId='lnk-singInForm-signUp'>
                <Text color='primary' typography='body2'>Daftar Akun</Text>
              </ButtonLink>
            </Text>
            <Divider gutter={16} />
            <Text
              typography='body2'
              textAlign='center'
              component='p'
              color='subtle'
            >
              Butuh Bantuan atau Solusi ? Silakan {' '}
              <Link href={waPinfinance2}><a className={styles.link}>Hubungi Kami</a></Link>
            </Text>
            {enable_modify_registration_flow &&
              <>
                <br />
                <div className={styles['login-form__section_center']}>
                  <Text typography='body3' color='subtle'>
                    Layanan Komisi Instan dari Pinhome Financial didukung oleh PT Indo Fin Tek yang terdaftar dan diawasi oleh OJK.
                  </Text>
                  <div className={styles['login-form__partners']}>
                    <div className={styles['login-form__partner-logo']}>
                      <Image
                        src={dompetKilatLogoUrl}
                        layout='fill'
                        objectFit='contain' alt='dompet-kilat-logo' />
                    </div>
                    <div className={styles['login-form__partner-logo']}>
                      <Image
                        src={ojkLogoUrl}
                        layout='fill'
                        objectFit='contain' alt='ojk-logo' />
                    </div>
                  </div>
                  <Text typography='body3' color='subtle'>
                    © 2022 - {new Date().getFullYear()} Pinhome Financial
                  </Text>
                </div>
              </>
            }
          </form>}
        {step == 'otp-verification' &&
          <section className={styles['login-page__otp']}>
            <OTPForm
              resendOtp={handleRequestOtp}
              phoneNumber={`+62${phoneNumber}`}
              verificationMethod={verificationTypeMapByValue(verificationMethod)}
              verifyOtp={handleVerifyOtp} errorVerifyOtp={errorVerifyOtp} />
          </section>
        }
      </CenterContentTemplate>
    </AppTemplate>
  );
};

export default LoginPage;
